import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { TaxJurisdiction } from "@moonscape/shared";
import { cn } from "@/utils/shadcn";
import { CheckMark, Lab, NUM, Pill } from "@/components/ms";

export interface FilingStep {
  label: string;
  completed: boolean;
  /** Where the CTA goes when this is the first incomplete step. */
  to?: string;
  /** CTA label when this is the first incomplete step (defaults to the step label). */
  cta?: string;
}

interface FilingBannerProps {
  taxYear: number;
  jurisdiction?: string;
  steps: FilingStep[];
  onGenerateReports?: () => void;
  reportsLink?: string;
}

function getNextDeadline(
  taxYear: number,
  jurisdiction?: string
): { date: Date; title: string; passed?: boolean } {
  const filingYear = taxYear + 1;
  const now = new Date();

  if (jurisdiction === TaxJurisdiction.UK) {
    const paperDeadline = new Date(filingYear, 9, 31);
    const onlineDeadline = new Date(filingYear + 1, 0, 31);
    if (now < paperDeadline) {
      return { date: paperDeadline, title: "Paper Filing Deadline" };
    }
    if (now < onlineDeadline) {
      return { date: onlineDeadline, title: "Online Filing Deadline" };
    }
    return { date: onlineDeadline, title: `${taxYear} Filing Complete`, passed: true };
  }

  // US / default
  const filingDeadline = new Date(filingYear, 3, 15);
  const extensionDeadline = new Date(filingYear, 9, 15);
  if (now < filingDeadline) {
    return { date: filingDeadline, title: `${taxYear} Tax Filing Deadline` };
  }
  if (now < extensionDeadline) {
    return { date: extensionDeadline, title: `${taxYear} Extension Deadline` };
  }
  return { date: extensionDeadline, title: `${taxYear} Deadline Passed`, passed: true };
}

export const FilingBanner: React.FC<FilingBannerProps> = ({
  taxYear,
  jurisdiction,
  steps,
  onGenerateReports,
  reportsLink,
}) => {
  const { daysLeft, deadlineTitle, deadlineStr, isPassed } = useMemo(() => {
    const { date, title, passed } = getNextDeadline(taxYear, jurisdiction);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diff = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    return {
      daysLeft: Math.max(0, diff),
      deadlineTitle: title,
      deadlineStr: date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      isPassed: !!passed,
    };
  }, [taxYear, jurisdiction]);

  const completedCount = steps.filter((s) => s.completed).length;

  return (
    <div className="flex flex-col gap-5 border border-t-[3px] border-ms-line border-t-ms-rule p-[18px] ms700:flex-row ms700:items-center ms700:gap-8">
      {/* Countdown */}
      <div className="flex flex-none items-baseline gap-3 ms700:flex-col ms700:items-start ms700:gap-1">
        {isPassed ? (
          <>
            <span className="inline-flex text-ms-green">
              <CheckMark size={28} />
            </span>
            <Lab className="text-ms-green">Filed</Lab>
          </>
        ) : (
          <>
            <span
              className={cn(
                NUM,
                "text-[44px] font-semibold leading-none tracking-[-0.035em] text-ms-tan"
              )}
            >
              {daysLeft}
            </span>
            <Lab className="text-ms-tan">Days left</Lab>
          </>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <h2 className="text-[16px] font-semibold tracking-[-0.02em] text-ms-ink">
          {deadlineTitle}
        </h2>
        <Lab size={9} className="mt-1 block text-ms-dim">
          {deadlineStr} · {completedCount}/{steps.length} steps complete
        </Lab>

        {/* Checklist steps */}
        <div className="mt-3.5 flex flex-wrap gap-x-5 gap-y-2.5">
          {steps.map((step, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-2 text-[13px]",
                step.completed ? "text-ms-green" : "text-ms-mute"
              )}
            >
              <span
                className={cn(
                  "grid h-[16px] w-[16px] flex-none place-items-center rounded-full",
                  step.completed
                    ? "bg-ms-mint-soft text-ms-green"
                    : "border border-ms-line2"
                )}
              >
                {step.completed && <CheckMark size={9} />}
              </span>
              <span>{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA — the first incomplete step that carries a route wins; otherwise reports */}
      {(() => {
        const next = steps.find((s) => !s.completed && s.to);
        if (next) {
          return (
            <Pill as={Link} to={next.to} className="flex-none">
              {next.cta ?? next.label}
            </Pill>
          );
        }
        if (reportsLink) {
          return (
            <Pill as={Link} to={reportsLink} className="flex-none">
              Generate reports
            </Pill>
          );
        }
        if (onGenerateReports) {
          return (
            <Pill onClick={onGenerateReports} className="flex-none">
              Generate reports
            </Pill>
          );
        }
        return null;
      })()}
    </div>
  );
};
